(() => {
  if (customElements.get('image-gallery')) return;

  class ImageGallery extends HTMLElement {
    connectedCallback() {
      this.popup = this.querySelector('.image-gallery__popup');
      this.modal = this.popup && this.popup.querySelector('.modal');
      this.closeBtn = this.popup && this.popup.querySelector('.image-gallery__close');
      this.swiperEl = (this.popup && this.popup.querySelector('swiper-container')) || this.querySelector('swiper-container');

      if (!this.popup || !this.modal) return;

      this.onGalleryClick = this.onGalleryClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onSlideChange = this.onSlideChange.bind(this);

      this.addEventListener('click', this.onGalleryClick);

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.onCloseClick);
      }

      if (this.swiperEl) {
        this.swiperEl.addEventListener('swiperslidechange', this.onSlideChange);
      }

      this.modal.addEventListener('click', this.onBackdropClick);
      document.addEventListener('keyup', this.onKeyup);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onGalleryClick);

      if (this.closeBtn) {
        this.closeBtn.removeEventListener('click', this.onCloseClick);
      }

      if (this.swiperEl) {
        this.swiperEl.removeEventListener('swiperslidechange', this.onSlideChange);
      }

      if (this.modal) {
        this.modal.removeEventListener('click', this.onBackdropClick);
      }

      document.removeEventListener('keyup', this.onKeyup);
      this.close();
    }

    get triggers() {
      return this.querySelectorAll('[data-gallery-trigger]');
    }

    onGalleryClick(event) {
      const trigger = event.target.closest('[data-gallery-trigger]');
      if (!trigger) return;

      event.preventDefault();
      const index = [...this.triggers].indexOf(trigger);
      this.open(Math.max(index, 0), trigger);
    }

    onCloseClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }

    onBackdropClick(event) {
      if (event.target === this.modal) this.close();
    }

    onKeyup(event) {
      if (!this.modal || !this.modal.classList.contains('open')) return;
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    }

    onSlideChange() {
      this.pauseVideos();
    }

    open(index, opener) {
      this.syncSlides();
      this.openedBy = opener;

      if (this.popup.parentElement !== document.body) {
        document.body.appendChild(this.popup);
      }

      this.modal.classList.add('open');
      this.popup.setAttribute('aria-hidden', 'false');

      this.showOverlay();

      if (typeof Utility !== 'undefined') {
        Utility.trapFocus(this.popup);
        if (this.closeBtn) Utility.forceFocus(this.closeBtn);
      }

      this.goToSlide(index);
    }

    close() {
      if (!this.modal || !this.popup) return;

      this.pauseVideos();
      this.modal.classList.remove('open');
      this.popup.setAttribute('aria-hidden', 'true');
      this.hideOverlay();

      if (typeof Utility !== 'undefined') {
        Utility.removeTrapFocus(this.openedBy);
      }

      if (this.popup.parentElement === document.body && this.isConnected) {
        this.appendChild(this.popup);
      } else if (this.popup.parentElement === document.body) {
        this.popup.remove();
      }
    }

    showOverlay() {
      const overlay = document.querySelector('#site-overlay');
      if (overlay) {
        overlay.classList.add('overlay--body');
        overlay.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('scroll-fixed');
    }

    hideOverlay() {
      const overlay = document.querySelector('#site-overlay');
      if (overlay) {
        overlay.classList.remove('overlay--body');
        overlay.setAttribute('aria-hidden', 'true');
      }
      document.body.classList.remove('scroll-fixed');
    }

    getSlideSrc(trigger) {
      const fullSrc = trigger.getAttribute('data-full-src');
      if (fullSrc) return fullSrc;

      const image = trigger.querySelector('img');
      if (!image) return '';

      const dataSrc = image.getAttribute('data-src');
      const src = image.currentSrc || image.getAttribute('src') || '';
      const isPlaceholder = (value) => value && (value.indexOf('animated-placeholder') !== -1 || value.indexOf('placeholder') !== -1);

      if (dataSrc && !isPlaceholder(dataSrc)) return dataSrc;
      if (src && !isPlaceholder(src)) return src;
      return dataSrc || '';
    }

    buildVideoSlide(trigger, videoSrc) {
      const poster = trigger.getAttribute('data-full-src');
      const video = document.createElement('video');

      video.className = 'w-100';
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (poster) video.poster = poster;

      const source = document.createElement('source');
      source.src = videoSrc;
      source.type = 'video/mp4';
      video.appendChild(source);

      return video;
    }

    buildImageSlide(trigger) {
      const src = this.getSlideSrc(trigger);
      if (!src) return null;

      const image = document.createElement('img');
      image.className = 'w-100';
      image.src = src;
      image.alt = trigger.getAttribute('data-full-alt') || '';

      return image;
    }

    syncSlides() {
      if (!this.swiperEl) return;

      this.swiperEl.querySelectorAll('swiper-slide').forEach((slide) => slide.remove());

      this.triggers.forEach((trigger) => {
        const videoSrc = trigger.getAttribute('data-video-src');
        const media = videoSrc ? this.buildVideoSlide(trigger, videoSrc) : this.buildImageSlide(trigger);
        if (!media) return;

        const slide = document.createElement('swiper-slide');
        slide.className = 'image-gallery__slide';
        if (videoSrc) slide.classList.add('image-gallery__slide--video');
        slide.appendChild(media);
        this.swiperEl.appendChild(slide);
      });

      if (this.swiperEl.swiper) {
        this.swiperEl.swiper.update();
      } else if (typeof this.swiperEl.initialize === 'function') {
        this.swiperEl.initialize();
      }
    }

    pauseVideos() {
      if (!this.swiperEl) return;

      this.swiperEl.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    playSlideVideo(index) {
      if (!this.swiperEl) return;

      const slide = this.swiperEl.querySelectorAll('swiper-slide')[index];
      const video = slide && slide.querySelector('video');
      if (!video) return;

      const played = video.play();
      // Browsers reject playback when the media is not ready yet; controls stay available.
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }

    goToSlide(index) {
      const updateSwiper = (attempts = 0) => {
        const swiper = this.swiperEl && this.swiperEl.swiper;

        if (swiper) {
          swiper.update();
          swiper.slideTo(index, 0);
          requestAnimationFrame(() => this.playSlideVideo(index));
          return;
        }

        if (this.swiperEl && typeof this.swiperEl.initialize === 'function' && attempts === 0) {
          this.swiperEl.initialize();
        }

        if (attempts < 30) {
          requestAnimationFrame(() => updateSwiper(attempts + 1));
        }
      };

      requestAnimationFrame(() => updateSwiper());
    }
  }

  customElements.define('image-gallery', ImageGallery);
})();
